/**
 * The People view registry: every list of people is one view of one table. A view declares its
 * title, its columns, how it counts itself and how its emails are copied; `sources.ts` declares
 * where its rows come from.
 */
import type { EventGroup, MemberStatus } from "../../../api/dashboard.types";
import type { StandingKey } from "../../data/types";
import { plural } from "./format";
import { LICENCES } from "./views/licences";
import { RENEWALS } from "./views/renewals";
import { RECENTLY_ADDED } from "./views/recentlyAdded";
import { CE21_FIXES } from "./views/ce21Fixes";
import { STATE_EMAILS } from "./views/stateEmails";
import { WIN_BACK } from "./views/winBack";

/** One person as the table shows them, whatever API they came from. */
export interface PersonLine {
  /** The member id. A registrant Keystone does not mirror has none: their row carries the negative of its order line instead, unique in the list and never a member's, with `linked: false`. */
  id: number;
  /** false: no person page and no member record (an unmirrored registrant). The row has no link, J/K skip it, and the server's export cannot include it. */
  linked?: false;
  /** The row's own key where one person can have two rows (an arrival in February and another in September). */
  key?: string;
  name: string;
  /** "" when the source gives none (the desk lists carry no address). */
  email: string;
  /** undefined: not read yet (the first part of a list whose standings are still on their way). */
  standing: StandingKey | null | undefined;
  /** Display value per column key, already formatted. */
  cells: Record<string, string>;
  /** A queue case somebody has already worked: the row settles to muted ink. */
  done?: boolean;
  /** A source-specific group key, for the view's count facts. */
  tag?: string;
}

/** A row's key in the table: unique even when a person has two rows. Ticks and the open row go by it. */
export const rowKey = (row: PersonLine): string => row.key ?? String(row.id);

export type ColumnKind = "text" | "standing" | "date" | "num" | "review";

export interface Column {
  /** Key into `PersonLine.cells`, or "standing". */
  key: string;
  label: string;
  kind?: ColumnKind;
  /** A text column that takes the room the others leave (a reason, a long header). */
  wide?: boolean;
  /** Kept when every row reads the same: the figure is what the list is about (a wave and its day), which the title does not say. */
  always?: true;
  /** Another cell shown under this one's value (a compact table's date, with its days). */
  under?: string;
  /** The cell counts days from today, written "Today", "1 day", "2 days", one unit down the column (a bare 0 reads like a missing value). */
  relative?: true;
}

/** A page of rows. Views that are not `paged` always return every row. */
export interface Page {
  rows: PersonLine[];
  total: number;
}

/** A count in the count line. `to` makes it a link: the query that opens exactly those people, merged into the address (`kind=Joined`, or `view=all&expiringWithin=30` for another view). */
export type Fact = readonly [count: number, label: string, to?: string];

export interface ViewDef {
  id: string;
  title: string;
  /** The switcher's word for the view when its title is longer than three words ("the group heading Email already says where"). */
  menu?: string;
  /** true: the server pages the list and searches it. false: loaded whole, searched in place. */
  paged: boolean;
  /** "segment": the server's reachable list (opt-outs and deceased removed). "rows": the rows' own addresses. "none": no list of addresses is offered (the people who opted out, and those held because a namesake has opted out). */
  emails: "segment" | "rows" | "none";
  columns: Column[];
  /** The line shown when the view has nobody. */
  empty: string;
  facts?: (rows: PersonLine[]) => Fact[];
  /** The `district` filter of a paged view. */
  district?: number;
  /** The `status` filter of a paged view. */
  status?: MemberStatus;
  /** What a row is, when it is not one person: an arrival (a person can join and, later, rejoin in the same window). */
  noun?: readonly [one: string, many: string];
  /** A worked list (a NEEDS YOU queue, win-back): it opens on its open cases, the number the sidebar shows; the reviewed ones are behind the Filter. A list without this opens whole, the number Today shows. */
  queue?: true;
  /** The columns below the `sm` breakpoint, when the list cannot show them all (Licences: the date and its days, #635). */
  compact?: Column[];
  /** One group of an event's people (#705): the members list's `event` and `eventGroup` filter. Its groups are its filters, so it has no Filter of its own. */
  event?: { ref: string; group: EventGroup };
}

const STANDING: Column = { key: "standing", label: "Standing", kind: "standing" };

const DISTRICT: Column = { key: "district", label: "District", kind: "num" };
const REVIEW: Column = { key: "review", label: "Review", kind: "review" };
const CONTACT: Column = { key: "contact", label: "Last contact", kind: "date" };
/** Days left in the lapse window, before the next step (the 65 days, then CE21's suspension at 120): what the lapsed lists call "Days to rejoin" and Today and the person's sheet call "days left". */
const LEFT: Column = { key: "left", label: "Days to rejoin", kind: "num", relative: true };

const MEMBER_COLUMNS: Column[] = [
  STANDING,
  { key: "tier", label: "Tier" },
  { key: "county", label: "County" },
  { key: "licence", label: "Licence", kind: "date" },
];

const STATIC: ViewDef[] = [
  {
    id: "all",
    title: "Everyone",
    paged: true,
    emails: "segment",
    columns: [STANDING, DISTRICT, ...MEMBER_COLUMNS.slice(1)],
    empty: "No one on file.",
  },
  {
    id: "active",
    title: "Active members",
    paged: true,
    emails: "segment",
    status: "active",
    columns: [STANDING, DISTRICT, ...MEMBER_COLUMNS.slice(1)],
    empty: "No active members.",
  },
  {
    id: "lapsed",
    title: "Lapsed",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, LEFT, CONTACT, REVIEW],
    empty: "No one is lapsed.",
  },
  {
    id: "lapsed-65",
    queue: true,
    title: "Lapsed within 65 days",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, LEFT, CONTACT, REVIEW],
    empty: "No one has lapsed lately.",
  },
  {
    id: "lapsed-120",
    queue: true,
    title: "Lapsed 66–120 days",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, LEFT, CONTACT, REVIEW],
    empty: "No one nears suspension.",
  },
  {
    id: "suspended",
    title: "Suspended",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, { key: "since", label: "Since", kind: "date" }, CONTACT, REVIEW],
    empty: "No one is suspended.",
  },
  {
    id: "at-risk",
    title: "At risk",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, { key: "reason", label: "Reason" }, REVIEW],
    empty: "No one is at risk.",
    // Every payment problem the list holds, reviewed or not: the rows with that reason, and the queue opened whole (`review=` is no pick) to show the same rows.
    facts: (rows) => [[rows.filter((r) => r.tag === "payment").length, "payment problems", "view=payment-issues&review="]],
  },
  {
    id: "payment-issues",
    queue: true,
    title: "Payment problems",
    paged: false,
    emails: "segment",
    columns: [STANDING, DISTRICT, CONTACT, REVIEW],
    empty: "No payment problems.",
  },
  {
    id: "licence-changes",
    queue: true,
    title: "Licence changes",
    paged: false,
    emails: "segment",
    columns: [
      { key: "state", label: "State expires", kind: "date" },
      { key: "ce21", label: "CE21 expires", kind: "date" },
      REVIEW,
    ],
    empty: "No licence differs.",
  },
  {
    id: "arrivals",
    title: "Joined and rejoined",
    noun: ["arrival", "arrivals"],
    paged: false,
    emails: "rows",
    columns: [STANDING, DISTRICT, { key: "kind", label: "Kind" }, { key: "date", label: "Date", kind: "date" }],
    empty: "No one joined or rejoined.",
    facts: (rows) => [
      [rows.filter((r) => r.tag === "joined").length, "joined", "kind=Joined"],
      [rows.filter((r) => r.tag === "rejoined").length, "rejoined", "kind=Rejoined"],
      [rows.filter((r) => r.tag === "unknown").length, "before history starts", "kind=Unknown"],
    ],
  },
  {
    id: "held",
    title: "Held for review",
    paged: false,
    emails: "none",
    columns: [{ key: "why", label: "Why", wide: true }, { key: "matches", label: "Same name", wide: true }, STANDING],
    empty: "Nobody is held.",
  },
  {
    id: "opted-out",
    title: "Opted out",
    paged: false,
    emails: "none",
    columns: [STANDING, { key: "master", label: "Master list" }, { key: "reason", label: "Reason" }, { key: "date", label: "Date", kind: "date" }],
    empty: "Nobody has opted out.",
  },
  {
    id: "not-in-ac",
    title: "Not in ActiveCampaign",
    paged: false,
    emails: "rows",
    columns: [STANDING, { key: "group", label: "Group" }, { key: "match", label: "Namesake" }, { key: "date", label: "Last activity", kind: "date" }],
    empty: "Everyone is in ActiveCampaign.",
  },
  LICENCES,
  STATE_EMAILS,
  CE21_FIXES,
  RENEWALS,
  WIN_BACK,
  RECENTLY_ADDED,
];

export const DISTRICTS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

const EVENT_PREFIX = "event-";
const DISTRICT_PREFIX = "district-";

export const DEFAULT_VIEW = "all";

/** The event of an old event list's address (`?view=event-<ref>`, #695): it is the event's page now. */
export function eventRef(viewId: string): string | null {
  return viewId.startsWith(EVENT_PREFIX) && viewId.length > EVENT_PREFIX.length ? viewId.slice(EVENT_PREFIX.length) : null;
}

/** The empty line of each group of an event's people. */
const EVENT_EMPTY: Record<EventGroup, string> = {
  everyone: "No one has registered yet.",
  members: "No members registered.",
  non_members: "No non-members registered.",
  vendors: "No vendors for this event.",
  came_last_time: "Everyone from last time has registered.",
};

/** One group of an event's people, as a list on the members API: the People table, its search, Copy emails and Export CSV, with no table of its own. */
export function eventPeopleView(ref: string, group: EventGroup): ViewDef {
  return {
    id: `${EVENT_PREFIX}${ref}:${group}`,
    title: "Event",
    paged: true,
    emails: "segment",
    event: { ref, group },
    columns: [STANDING, DISTRICT, ...MEMBER_COLUMNS.slice(1)],
    empty: EVENT_EMPTY[group],
  };
}

export function districtViewId(n: number): string {
  return `${DISTRICT_PREFIX}${n}`;
}

/** The definition for a view id, or null when no such view exists. */
export function resolveView(id: string): ViewDef | null {
  const fixed = STATIC.find((v) => v.id === id);
  if (fixed) return fixed;

  const match = /^district-(\d+)$/.exec(id);
  const n = match ? Number(match[1]) : 0;
  if (n >= 1 && n <= 8) {
    return {
      id,
      title: `District ${n}`,
      paged: true,
      emails: "segment",
      district: n,
      columns: MEMBER_COLUMNS,
      empty: "No one in this district.",
    };
  }
  return null;
}

/**
 * The views the switcher lists, in order: three short groups (30 words in all). The five queues (payment problems,
 * both lapsed windows, licence changes, held for review) are not here: they are NEEDS YOU in the sidebar, one click away.
 * An event's people are under Events, a district is Everyone's District facet; ⌘K opens any.
 */
export const VIEW_GROUPS: ReadonlyArray<{ label: string; ids: readonly string[] }> = [
  { label: "Standing", ids: ["all", "active", "lapsed", "suspended", "at-risk"] },
  { label: "Changes", ids: ["arrivals", "licences", "renewals", "win-back"] },
  { label: "Email", ids: ["opted-out", "not-in-ac", "recently-added-ac"] },
];

export function staticTitle(id: string): string {
  return STATIC.find((v) => v.id === id)?.title ?? id;
}

/** What the view switcher calls a view: its short word when it has one. */
export function menuTitle(id: string): string {
  const view = STATIC.find((v) => v.id === id);
  return view?.menu ?? view?.title ?? id;
}

/** True when the table needs the 36 px dense rows: more than 6 columns including the name. */
export function isDense(view: ViewDef): boolean {
  return view.columns.length + 1 > 6;
}

/**
 * The one count line: "71 people" (a queue shows what is open, the number Today shows), "31 people · 12 joined"
 * when the view has facts, "3 of 90 people" once a search or a filter has narrowed a list that is held whole.
 * `rows` are the ones the facts count, `count` how many people the list holds now, `of` the whole list when narrowed.
 */
export function countLine(view: ViewDef, rows: PersonLine[], count: number, of: number | null = null): string {
  const { head, facts } = countParts(view, rows, count, of);
  return [head, ...facts.map((f) => f.text)].join(" · ");
}

/** The count line in its pieces: the people, then the facts, each with the query that opens just those people. */
export function countParts(view: ViewDef, rows: PersonLine[], count: number, of: number | null = null): { head: string; facts: Array<{ text: string; to?: string }> } {
  const [one, many] = view.noun ?? ["person", "people"];
  if (of !== null && count !== of) return { head: `${count.toLocaleString("en-US")} of ${plural(of, one, many)}`, facts: [] };
  // A fact that counts the whole list says nothing the head does not ("11 people · 11 rejoined").
  const facts = (view.facts?.(rows) ?? []).filter(([n]) => n > 0 && n !== count).map(([n, label, to]) => ({ text: `${n.toLocaleString("en-US")} ${label}`, to }));
  return { head: plural(count, one, many), facts };
}

/** Search in place: name, address, district and the cells, case-insensitive, every word must match. */
export function matches(row: PersonLine, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = `${row.name} ${row.email} ${Object.values(row.cells).join(" ")}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}
